import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ClipboardList, ShieldCheck } from 'lucide-react';
import { apiFetch, v1Path } from '../../lib/api';
import { formatDateOnly } from '../../lib/dates';
import { serviceRequestPath } from '../../lib/service-requests';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

export type MyApprovalRow = {
  id: string;
  key: string;
  title: string;
  project_id: string;
  customer: string | null;
  request_type_name: string | null;
  phase: 'initial' | 'deployment';
  role: string;
  waiting_days: number | null;
  target_go_live_date: string | null;
};

/** Service Requests waiting on a role the signed-in person approves for. Refetches when `refreshKey` changes. */
export function useMyApprovals(refreshKey?: unknown): MyApprovalRow[] | null {
  const [items, setItems] = useState<MyApprovalRow[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    apiFetch<{ items: MyApprovalRow[] }>(v1Path('/service-requests/my-approvals'))
      .then((res) => { if (!cancelled) setItems(Array.isArray(res.items) ? res.items : []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [refreshKey]);
  return items;
}

export type MyRequestRow = {
  id: string;
  key: string;
  title: string;
  project_id: string;
  customer: string | null;
  request_type_name: string | null;
  stage: string | null;
  stage_name: string | null;
  waiting_on: string | null;
  action: 'submit' | 'resubmit' | null;
  requirements_percent: number;
  days_in_stage: number | null;
};

/** Open Service Requests the signed-in person raised; the server sorts changes-requested first, drafts last. */
export function useMyRequests(): MyRequestRow[] | null {
  const [items, setItems] = useState<MyRequestRow[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    apiFetch<{ items: MyRequestRow[] }>(v1Path('/service-requests/my-requests'))
      .then((res) => { if (!cancelled) setItems(Array.isArray(res.items) ? res.items : []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, []);
  return items;
}

function waitingText(days: number | null): string {
  if (days === null) return '';
  if (days === 0) return 'Waiting since today';
  return `Waiting ${days} day${days === 1 ? '' : 's'}`;
}

/** Home and Service Requests banner; renders nothing when no request is waiting on you. Items arrive oldest first. */
export function NeedsApprovalCard() {
  const items = useMyApprovals();
  const [expanded, setExpanded] = useState(false);
  if (!items || items.length === 0) return null;
  const count = items.length;
  const oldest = items[0];
  return (
      <Card>
        <div className="flex flex-wrap items-center gap-3 px-4 py-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
            <ShieldCheck className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">
              {count} request{count === 1 ? ' needs' : 's need'} your approval
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {[
                count === 1 ? `${oldest.key} ${oldest.title}` : `Oldest: ${oldest.key} ${oldest.title}`,
                `${oldest.phase === 'initial' ? 'Initial' : 'Deployment'} approval · ${oldest.role}`,
                waitingText(oldest.waiting_days),
              ].filter(Boolean).join(' · ')}
            </p>
          </div>
          {count > 1 ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => setExpanded((open) => !open)} aria-expanded={expanded}>
              {expanded ? 'Hide' : `See all ${count}`}
            </Button>
          ) : null}
          <Button asChild size="sm">
            <Link to={serviceRequestPath({ id: oldest.id, project_id: oldest.project_id })} className="hover:no-underline">
              {count === 1 ? 'Review request' : 'Review oldest'}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </Button>
        </div>
        {expanded ? (
          <ul className="divide-y border-t px-4">
            {items.map((row) => (
              <li key={`${row.id}-${row.phase}`}>
                <Link
                  to={serviceRequestPath({ id: row.id, project_id: row.project_id })}
                  className="group flex items-center gap-3 py-2.5 hover:no-underline"
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{row.key}</span>
                      <span className="truncate text-sm font-medium text-foreground group-hover:text-primary">{row.title}</span>
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {[
                        row.customer,
                        row.request_type_name,
                        `${row.phase === 'initial' ? 'Initial' : 'Deployment'} approval · ${row.role}`,
                        row.target_go_live_date ? `Target ${formatDateOnly(row.target_go_live_date) ?? row.target_go_live_date}` : null,
                      ].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{waitingText(row.waiting_days)}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </Card>
  );
}

const YOUR_REQUESTS_SHOWN = 5;

function RequestStatus({ row }: { row: MyRequestRow }) {
  if (row.action === 'resubmit') {
    return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-200">Changes requested · update and resubmit</span>;
  }
  if (row.action === 'submit') {
    return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">Draft · {row.requirements_percent}% done · not submitted</span>;
  }
  if (row.waiting_on) {
    return <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800 dark:bg-sky-950 dark:text-sky-200">Waiting on {row.waiting_on}{row.days_in_stage ? ` · ${row.days_in_stage}d` : ''}</span>;
  }
  return <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">{row.stage_name ?? 'Open'}</span>;
}

/** Home page card for requestors; renders nothing when they have no open requests. */
export function YourRequestsCard() {
  const items = useMyRequests();
  if (!items || items.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ClipboardList className="h-4 w-4 text-primary" aria-hidden />
          Your service requests
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">{items.length}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="divide-y">
          {items.slice(0, YOUR_REQUESTS_SHOWN).map((row) => (
            <li key={row.id}>
              <Link
                to={serviceRequestPath({ id: row.id, project_id: row.project_id })}
                className="group flex items-center gap-3 py-2.5 hover:no-underline"
              >
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span className="font-mono text-xs text-muted-foreground">{row.key}</span>
                    <span className="truncate text-sm font-medium text-foreground group-hover:text-primary">{row.title}</span>
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {[row.customer, row.request_type_name].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className="hidden shrink-0 sm:inline"><RequestStatus row={row} /></span>
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
        {items.length > YOUR_REQUESTS_SHOWN ? (
          <Link to="/service-requests" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:no-underline">
            View all {items.length} <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </Link>
        ) : null}
      </CardContent>
    </Card>
  );
}

const MY_WORK_CARD_CLASS =
  'overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0_1px_2px_hsl(var(--brand-navy)/0.04),0_4px_12px_-6px_hsl(var(--brand-navy)/0.08)]';

function requestStage(row: MyRequestRow): { label: string; dot: string; detail: string | null } {
  if (row.action === 'submit') return { label: 'Draft', dot: 'bg-slate-400', detail: null };
  if (row.action === 'resubmit') return { label: 'Changes requested', dot: 'bg-amber-500', detail: null };
  const days = row.days_in_stage ? ` · ${row.days_in_stage}d` : '';
  return {
    label: row.stage_name ?? 'Open',
    dot: 'bg-sky-500',
    detail: row.waiting_on ? `With ${row.waiting_on}${days}` : null,
  };
}

function requestAction(row: MyRequestRow): string {
  if (row.action === 'submit') return 'Continue';
  if (row.action === 'resubmit') return 'Update';
  return 'View';
}

/** The My work page's requests: stage, how much of the form is filled in, and the next step. */
export function MyRequestsSection({ items }: { items: MyRequestRow[] | null }) {
  if (!items || items.length === 0) return null;
  return (
    <section aria-labelledby="my-requests-title">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 id="my-requests-title" className="font-display text-base font-extrabold tracking-tight">
          My requests
        </h2>
        <Link to="/service-requests" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:no-underline">
          View all requests <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
      <div className={MY_WORK_CARD_CLASS}>
        <table className="w-full table-fixed text-left text-sm">
          <thead>
            <tr className="border-b border-border/70 text-xs font-medium text-muted-foreground">
              <th scope="col" className="px-4 py-2.5 font-medium">Request</th>
              <th scope="col" className="hidden w-44 px-3 py-2.5 font-medium sm:table-cell">Stage</th>
              <th scope="col" className="hidden w-64 px-3 py-2.5 font-medium md:table-cell">Requirements</th>
              <th scope="col" className="w-28 px-4 py-2.5"><span className="sr-only">Next step</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/70">
            {items.map((row) => {
              const to = serviceRequestPath({ id: row.id, project_id: row.project_id });
              const stage = requestStage(row);
              const percent = Math.max(0, Math.min(100, Math.round(row.requirements_percent ?? 0)));
              return (
                <tr key={row.id}>
                  <td className="px-4 py-3">
                    <Link to={to} className="block truncate font-semibold text-foreground hover:text-primary hover:no-underline">
                      {row.title}
                    </Link>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      {[row.key, row.request_type_name].filter(Boolean).join(' · ')}
                    </p>
                  </td>
                  <td className="hidden px-3 py-3 sm:table-cell">
                    <span className="flex items-center gap-2 text-foreground">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${stage.dot}`} aria-hidden />
                      <span className="truncate">{stage.label}</span>
                    </span>
                    {stage.detail ? <p className="mt-0.5 truncate pl-4 text-xs text-muted-foreground">{stage.detail}</p> : null}
                  </td>
                  <td className="hidden px-3 py-3 md:table-cell">
                    <span className="flex items-center gap-3">
                      <span
                        className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"
                        role="progressbar"
                        aria-label={`${row.title} requirements`}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={percent}
                      >
                        <span className="block h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
                      </span>
                      <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{percent}%</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={to} className="inline-flex items-center gap-1.5 font-medium text-primary hover:no-underline" aria-label={`${requestAction(row)} ${row.title}`}>
                      {requestAction(row)} <ArrowRight className="h-4 w-4" aria-hidden />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
